import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import api from '../api';
import CommunityAvatarViewer from '../components/CommunityAvatarViewer';
import { canAccessStudio } from '../studio/studioAccess';

import './CommunityAvatars.css';

const PAGE_SIZE = 20;

function CreationCard({ creation, onOpen }) {
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setFailed(false);
  }, [creation.imageUrl]);

  return (
    <button
      type="button"
      className="community-grid-card"
      onClick={() => onOpen(creation._id)}
      aria-label={`View ${creation.name || 'avatar'}`}
    >
      <div className="community-grid-card__image">
        {failed || !creation.imageUrl ? (
          <span>Image unavailable</span>
        ) : (
          <img
            src={creation.imageUrl}
            alt={creation.name || 'Community avatar'}
            loading="lazy"
            decoding="async"
            draggable={false}
            onError={() => setFailed(true)}
          />
        )}
      </div>

      <span
        className="community-grid-card__name"
        title={creation.name}
      >
        {creation.name || 'Untitled avatar'}
      </span>
    </button>
  );
}

export default function CommunityAvatars({ user }) {
  const navigate = useNavigate();
  const location = useLocation();

  const accountKey = String(user?._id || user?.username || '');

  const [creations, setCreations] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedId, setSelectedId] = useState(null);

  const requestRef = useRef(null);
  const generationRef = useRef(0);
  const busyRef = useRef(false);

  useEffect(() => {
    const generation = ++generationRef.current;

    requestRef.current?.abort();
    busyRef.current = false;

    setCreations([]);
    setNextCursor(null);
    setHasMore(false);
    setSelectedId(null);
    setError('');

    if (!accountKey) {
      setLoading(false);
      return undefined;
    }

    // The cleanup cancels both initial and Load more requests.
    loadPage(null, true, generation);

    return () => {
      generationRef.current += 1;
      requestRef.current?.abort();
      busyRef.current = false;
    };
    // Pagination is started explicitly by the Load more button.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountKey]);

  async function loadPage(
    cursor,
    replace = false,
    generation = generationRef.current
  ) {
    if (busyRef.current) return;

    const controller = new AbortController();
    requestRef.current = controller;
    busyRef.current = true;

    setLoading(true);
    setError('');

    try {
      const { data } = await api.get('/community-avatars/all', {
        params: {
          limit: PAGE_SIZE,
          ...(cursor ? { cursor } : {}),
        },
        signal: controller.signal,
      });

      if (
        controller.signal.aborted ||
        generation !== generationRef.current
      ) {
        return;
      }

      if (!Array.isArray(data?.creations)) {
        throw new Error('Invalid community avatar response.');
      }

      const incoming = data.creations.filter(
        (creation) => creation?._id
      );

      setCreations((previous) => {
        const combined = replace
          ? incoming
          : [...previous, ...incoming];

        const seen = new Set();

        return combined.filter((creation) => {
          const id = String(creation._id);
          if (seen.has(id)) return false;

          seen.add(id);
          return true;
        });
      });

      const newCursor =
        typeof data.nextCursor === 'string'
          ? data.nextCursor
          : null;

      setNextCursor(newCursor);
      setHasMore(
        data.hasMore === true &&
        Boolean(newCursor) &&
        newCursor !== cursor
      );
    } catch (requestError) {
      if (
        controller.signal.aborted ||
        generation !== generationRef.current
      ) {
        return;
      }

      setError(
        requestError?.response?.status === 401
          ? 'Please sign in again to browse community avatars.'
          : 'Could not load avatars. Please try again.'
      );
    } finally {
      if (
        !controller.signal.aborted &&
        generation === generationRef.current
      ) {
        busyRef.current = false;
        setLoading(false);
      }
    }
  }

  function handleBack() {
    if (location.state?.from === 'home') {
      navigate('/home', {
        replace: true,
        state: { restoreHomeScroll: true },
      });
      return;
    }

    if (Number(window.history.state?.idx) > 0) {
      navigate(-1);
      return;
    }

    navigate('/home', { replace: true });
  }

  function handleCreateAvatar() {
    setSelectedId(null);

    navigate(
      canAccessStudio(user) ? '/studio/avatar' : '/edit-profile'
    );
  }

  function handleOpenProfile(username) {
    setSelectedId(null);

    navigate(
      username === user?.username
        ? '/profile'
        : `/user/${encodeURIComponent(username)}`
    );
  }

  const initialLoading = loading && creations.length === 0;

  return (
    <main className="community-avatars-screen">
      <header className="community-avatars-screen__header">
        <button
          type="button"
          className="community-avatars-screen__back"
          onClick={handleBack}
          aria-label="Go back"
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="m15 5-7 7 7 7"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        <h1>Community Avatars</h1>
      </header>

      <div className="community-avatars-screen__scroll">
        <div className="community-avatars-screen__content">
          <p className="community-avatars-screen__intro">
            Explore creations from the BarterFi community.
          </p>

          <div
            className="community-avatars-grid"
            aria-busy={loading}
          >
            {initialLoading
              ? Array.from({ length: 6 }, (_, index) => (
                  <div
                    key={`skeleton-${index}`}
                    className="community-grid-card community-grid-card--skeleton"
                    aria-hidden="true"
                  >
                    <div className="community-grid-card__image community-grid-shimmer" />

                    <div className="community-grid-card__skeleton-label">
                      <span className="community-grid-shimmer" />
                    </div>
                  </div>
                ))
              : creations.map((creation) => (
                  <CreationCard
                    key={creation._id}
                    creation={creation}
                    onOpen={setSelectedId}
                  />
                ))}
          </div>

          {initialLoading && (
            <p
              className="community-avatars-screen__status"
              role="status"
            >
              Loading avatars…
            </p>
          )}

          {error && (
            <div
              className="community-avatars-screen__status"
              role="status"
            >
              <p>{error}</p>

              <button
                type="button"
                className="community-avatars-screen__load"
                disabled={loading}
                onClick={() =>
                  loadPage(
                    creations.length ? nextCursor : null,
                    creations.length === 0
                  )
                }
              >
                Retry
              </button>
            </div>
          )}

          {!loading && !error && creations.length === 0 && (
            <div className="community-avatars-screen__status">
              <p>No community avatars yet.</p>

              <button
                type="button"
                className="community-avatars-screen__load"
                onClick={handleCreateAvatar}
              >
                Create my avatar
              </button>
            </div>
          )}

          {!error && hasMore && (
            <div className="community-avatars-screen__pagination">
              <button
                type="button"
                className="community-avatars-screen__load"
                disabled={loading}
                onClick={() => loadPage(nextCursor)}
              >
                {loading ? 'Loading…' : 'Load more'}
              </button>
            </div>
          )}

          {!loading && !error && !hasMore && creations.length > 0 && (
            <p className="community-avatars-screen__status">
              You’ve seen all the creations.
            </p>
          )}
        </div>
      </div>

      {selectedId && (
        <CommunityAvatarViewer
          key={selectedId}
          creationId={selectedId}
          onClose={() => setSelectedId(null)}
          onOpenProfile={handleOpenProfile}
          onCreateAvatar={handleCreateAvatar}
        />
      )}
    </main>
  );
}
